import { AppInfo, DevicePlatform } from "@/shared/types";
import Bowser from "bowser";

interface BrowserInfo {
  name: string;
  version: string;
}

const detectBrowser = (): BrowserInfo => {
  /**
   * https://stackoverflow.com/questions/5916900/how-can-you-detect-the-version-of-a-browser
   */
  const nav = window.navigator;
  const ua = nav.userAgent || "";
  let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

  if (/trident/i.test(M[1])) {
    const tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
    return { name: "IE", version: tem[1] || "" };
  }

  if (M[1] === "Chrome") {
    const tem = ua.match(/\b(OPR|Edg)\/(\d+)/i);
    if (tem) {
      return {
        name: tem[1].replace("OPR", "Opera").replace("Edg", "Edge"),
        version: tem[2]
      };
    }
  }

  M = M[2] ? [M[1], M[2]] : [nav.appName, nav.appVersion, "-?"];

  const tem = ua.match(/version\/(\d+)/i);
  if (tem) {
    M.splice(1, 1, tem[1]);
  }

  return { name: M[0], version: M[1] };
};

const detectOS = (): DevicePlatform => {
  const nav = window.navigator as Navigator & {
    userAgentData?: {
      platform?: string;
    };
  };

  if (nav.userAgentData && nav.userAgentData.platform) {
    const platform = nav.userAgentData.platform.toLowerCase();

    if (platform.includes("win")) return DevicePlatform.Windows;
    if (platform.includes("mac")) return DevicePlatform.macOS;
    if (platform.includes("android")) return DevicePlatform.Android;
    if (platform.includes("linux")) return DevicePlatform.Linux;
    if (platform.includes("chrome")) return DevicePlatform.ChromeOS;
    if (platform.includes("ios") || platform.includes("iphone") || platform.includes("ipad")) return DevicePlatform.iOS;
  }

  const ua = nav.userAgent || "";

  if (/iPhone|iPad|iPod/i.test(ua) || (ua.includes("Mac") && nav.maxTouchPoints > 1)) {
    return DevicePlatform.iOS;
  }

  if (/Android/i.test(ua)) {
    return DevicePlatform.Android;
  }

  if (/CrOS/i.test(ua)) {
    return DevicePlatform.ChromeOS;
  }

  if (/Windows NT/i.test(ua)) {
    return DevicePlatform.Windows;
  }

  if (/Macintosh|Mac OS X/i.test(ua)) {
    return DevicePlatform.macOS;
  }

  if (/Linux/i.test(ua)) {
    return DevicePlatform.Linux;
  }

  return DevicePlatform.Browser;
};

export const browserInfo = (): AppInfo => {
  const browser = detectBrowser();
  const os = detectOS();

  return {
    platform: os,
    name: browser.name,
    version: browser.version,
    isMobile: false
  };
};

export const getBrowserParser = () => {
  const nav = window.navigator as Navigator & {
    userAgentData?: Bowser.ClientHints;
  };

  return Bowser.getParser(nav.userAgent, nav.userAgentData);
};
